import { Injectable } from '@angular/core';
import { LocationService } from '@core/services/location.service';
import { PortalDeterminationService } from '@core/services/portal-determination.service';
import { TokenService } from '@core/services/token/token.service';
import { ContactSupport } from '@core/typings/ui/support.typing';
import { UserService } from '@features/users/user.service';
import { TypeaheadSelectOption } from '@yourcause/common/core-forms';
import { HeapService, TrackingPropertyNames, TrackingEventNames } from '@yourcause/common/heap';
import { I18nService } from '@yourcause/common/i18n';
import { ConfirmAndTakeActionService, ModalFactory } from '@yourcause/common/modals';
import { AttachYCState, BaseYCService } from '@yourcause/common/state';
import { ContactSupportModalComponent } from './contact-support-modal/contact-support-modal.component';
import { SupportResources } from './support.resources';
import { SupportState } from './support.state';
import { NotifierService } from '@yourcause/common/notifier';

@AttachYCState(SupportState)
@Injectable({ providedIn: 'root'})
export class SupportService extends BaseYCService<SupportState> {
  readonly contactReasonPreAuthOptions: TypeaheadSelectOption<ContactSupport.ContactReasonFullList>[] = [{
      label: this.i18n.translate('common:textUpdateMyEmail', {}, 'Update my email'),
      value: ContactSupport.ContactReasonFullList.UPDATE_EMAIL
    }, {
      label: this.i18n.translate('common:textChangeMyPassword', {}, 'Change my password'),
      value: ContactSupport.ContactReasonFullList.CHANGE_PASSWORD
    }, {
      label: this.i18n.translate('common:textOther', {}, 'Other'),
      value: ContactSupport.ContactReasonFullList.OTHER
    }];
  readonly contactReasonPostAuthOptions: TypeaheadSelectOption<ContactSupport.ContactReasonFullList>[] = [{
      label: this.i18n.translate('common:textForms', {}, 'Forms'),
      value: ContactSupport.ContactReasonFullList.FORMS
    }, {
      label: this.i18n.translate('common:textDataMissing', {}, 'Data missing'),
      value: ContactSupport.ContactReasonFullList.DATA_MISSING
    }, {
      label: this.i18n.translate('common:textApplicationNomination', {}, 'Application/Nomination'),
      value: ContactSupport.ContactReasonFullList.APPLICATION_NOMINATION
    }, {
      label: this.i18n.translate('common:textUnableToFindOrg', {}, 'Unable to find organization'),
      value: ContactSupport.ContactReasonFullList.UNABLE_TO_FIND_ORG
    }, {
      label: this.i18n.translate('common:textProfileInformation', {}, 'Profile information'),
      value: ContactSupport.ContactReasonFullList.PROFILE_INFO
    },
    ...this.contactReasonPreAuthOptions
  ];

   constructor (
    private i18n: I18nService,
    private supportResources: SupportResources,
    private tokenService: TokenService,
    private modalFactory: ModalFactory,
    private userService: UserService,
    private portal: PortalDeterminationService,
    private locationService: LocationService,
    private heapService: HeapService,
    private confirmAndTakeAction: ConfirmAndTakeActionService,
    private notifier: NotifierService
  ) {
    super();
  }

  get prepopData () {
    return this.get('prepopData');
  }

  get contactReasonOptions () {
    const isLoggedIn = this.tokenService.getIsLoggedIn();
    if (isLoggedIn) {
      return this.contactReasonPostAuthOptions;
    }

    return this.contactReasonPreAuthOptions;
  }

  setPrepopData (payload: ContactSupport.ContactSupportModalReturn) {
    this.set('prepopData', payload);
  }

  /**
   * Do the Contact Support Logic
   *
   * @param payload: Contact support payload
   */
  async doContactSupport (payload: ContactSupport.ContactSupportModalReturn) {
    await this.supportResources.sendSalesForceTicket({
      ...payload,
      reason: this.contactReasonOptions.find((option) => {
        return option.value === payload.reason;
      }).label
    });
    this.heapService.track(
      TrackingEventNames.ContactSupport,
      {}
    );
  }

  /**
   * Handle Contacting Support after submitting the modal
   *
   * @param payload: Contact support payload
   */
  async handleContactSupportModal (payload: ContactSupport.ContactSupportModalReturn) {
    await this.confirmAndTakeAction.genericTakeAction(
      () => this.doContactSupport(payload),
      this.i18n.translate(
        'common:textSupportTicketSuccessfullySubmitted',
        {},
        'Support ticket successfully submitted'
      ),
      this.i18n.translate(
        'common:textErrorSubmittingTicket',
        {},
        'There was an error submitting your support ticket'
      )
    );
  }

  trackClickAndOpenLink (eventName: string, link: string) {
    this.heapService.track(
      eventName,
      {
        [TrackingPropertyNames.Link]: link
      }
    );

    window.open(link, '_blank');
  }

  trackClick (eventName: string, link: string) {
    this.heapService.track(
      eventName,
      {
        [TrackingPropertyNames.Link]: link
      }
    );
  }

  async contactSupport () {
    const modalResponse = await this.modalFactory.open(
      ContactSupportModalComponent,
      {
        contactReasonPostAuthOptions: this.contactReasonOptions,
        prepopData: {
          ...this.prepopData,
          companySeekingGrantFrom: null,
          firstName: this.userService.currentUser?.firstName,
          lastName: this.userService.currentUser?.lastName,
          email: this.userService.currentUser?.email,
          phoneNumber: this.userService.applicant?.phoneNumber ?? '',
          questionDescription: ''
        }
      }
    );
    if (!!modalResponse) {
      await this.handleContactSupportModal(modalResponse);
    }
  }

  isLiveChatAvailable () {
    const chatButton = document.getElementById('embeddedMessagingConversationButton');
    if (!!chatButton) {
      return chatButton.style?.display !== 'none';
    }

    return false;
  }

  openLiveChat () {
    if (this.isLiveChatAvailable()) {
      const chatButton = document.getElementById('embeddedMessagingConversationButton');
      if (!!chatButton) {
        chatButton.classList.add('make-visible');
        chatButton.click();
      }
    } else {
      this.notifier.warning(this.i18n.translate(
        'common:textLiveChatNotAvailable',
        {},
        'Live chat is not available at this time.'
      ));
    }
  }

  getHelpCenterLink () {
    return this.portal.isManager ?
      'https://webfiles.blackbaud.com/files/support/helpfiles/grantsconnect/content/home.html' :
      'https://webfiles.blackbaud.com/files/support/helpfiles/grantsconnect/content/gc-applicants.html';
  }

  openHelpCenter () {
    const link = this.getHelpCenterLink();
    this.locationService.openExternalUrl(link);
  }
}

